import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"
import axios from "axios"

import { Pagination } from "components"
import SelectSearchable from "components/forms/select-searchable"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import useProductModelRepositories from "repositories/product-model"
import useSalesGroupReportRepositories from "repositories/sales-group-report"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const formatDateID = (value) => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const dateFrom = searchParams.get("date_from") || toInputDate(new Date(Date.now() - 86400000))
  const dateTo = searchParams.get("date_to") || toInputDate(new Date(Date.now() - 86400000))
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const groupId = searchParams.get("group_id") || "ALL"
  // ATALLA-287: filter sumber penjualan (ALL / ATALLA toko-POS).
  const source = searchParams.get("source") || "ALL"
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "50"

  // ── Group filter: server-side search (lazy fetch, paginated) ─────────
  const [groupSearchInput, setGroupSearchInput] = useState("")
  const [groupSearch, setGroupSearch] = useState("")
  const groupSearchTimerRef = useRef(null)

  const handleGroupSearchChange = (val) => {
    setGroupSearchInput(val)
    if (groupSearchTimerRef.current) clearTimeout(groupSearchTimerRef.current)
    groupSearchTimerRef.current = setTimeout(() => setGroupSearch(val), 350)
  }

  useEffect(() => () => {
    if (groupSearchTimerRef.current) clearTimeout(groupSearchTimerRef.current)
  }, [])

  const { data: modelData, isLoading: isLoadingModels } = useProductModelRepositories({
    paginate: true,
    limit: 50,
    search: groupSearch || undefined,
  })

  const modelSelection = useMemo(() => {
    if (Array.isArray(modelData?.data)) return modelData.data
    if (Array.isArray(modelData)) return modelData
    return []
  }, [modelData])

  // Preserve label for pre-selected group from URL (id is in URL but row may not be in current page)
  const [initialSelectedGroup, setInitialSelectedGroup] = useState(null)

  useEffect(() => {
    if (!groupId || groupId === "ALL") return
    if (initialSelectedGroup && String(initialSelectedGroup.id) === String(groupId)) return
    if (modelSelection.some((r) => String(r.id) === String(groupId))) return

    let cancelled = false
    axios
      .get(`${process.env.REACT_APP_INVENTORY_API_URL}/product/model/${groupId}`)
      .then((res) => {
        if (cancelled) return
        const row = res.data?.data
        if (row?.id) setInitialSelectedGroup(row)
      })
      .catch(() => { })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId])

  const groupOptions = useMemo(() => {
    const all = {
      id: "ALL",
      name: "Semua Group",
      description: "Gabungan semua category",
    }
    const list = [all, ...modelSelection]
    if (
      initialSelectedGroup &&
      String(initialSelectedGroup.id) !== "ALL" &&
      !list.some((r) => String(r.id) === String(initialSelectedGroup.id))
    ) {
      list.push(initialSelectedGroup)
    }
    return list
  }, [modelSelection, initialSelectedGroup])

  const { data: reportData, isLoading } = useSalesGroupReportRepositories({
    date_from: dateFrom,
    date_to: dateTo,
    outlet_id: resolvedOutletCsv || undefined,
    group_id: groupId,
    source,
    page,
    per_page: perPage,
    enabled: true,
  })

  const groups = useMemo(() => {
    if (Array.isArray(reportData?.items?.data)) return reportData.items.data
    return reportData?.groups ?? []
  }, [reportData])
  const summary = useMemo(() => reportData?.summary ?? {}, [reportData])
  const pagination = reportData?.items
  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []

    return links.map((link) => {
      if (!link?.url) return link

      const nextParams = new URLSearchParams(searchParams)

      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      }

      return {
        ...link,
        url: `?${nextParams.toString()}`,
      }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0

    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0

    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Periode wajib diisi sebelum export")
      return
    }

    const query = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
      group_id: groupId,
      source,
    })
    if (resolvedOutletCsv) query.set("outlet_id", resolvedOutletCsv)

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/sales-group/export?${query.toString()}`,
      "_blank"
    )
  }

  const showGrandTotal = !isLoading && groups.length > 0 && (pagination?.current_page ?? 1) === (pagination?.last_page ?? 1)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Laporan Penjualan Group</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Detail penjualan per faktur dikelompokkan per kategori untuk periode yang dipilih.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[200px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Outlet
            </label>
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeSearchParams({ outlet_id: v, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              title="Outlet"
            />
          </div>

          <div className="min-w-[170px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Sumber
            </label>
            <select
              value={source}
              onChange={(e) => mergeSearchParams({ source: e.target.value || "ALL", page: "1" })}
              className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-xl bg-white"
            >
              <option value="ALL">Semua Sumber</option>
              <option value="ATALLA">Atalla (Toko/POS)</option>
            </select>
          </div>

          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Group
            </label>
            <SelectSearchable
              selection={groupOptions}
              isLoading={isLoadingModels}
              value={groupId}
              onChange={(value) => mergeSearchParams({ group_id: value || "ALL", page: "1" })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={(v) => v.code || v.id}
              placeholder="Cari & pilih group..."
              serverSearch
              searchValue={groupSearchInput}
              onSearchChange={handleGroupSearchChange}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal Dari
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateFrom}
              onChange={(event) => mergeSearchParams({ date_from: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal Sampai
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateTo}
              onChange={(event) => mergeSearchParams({ date_to: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Per Halaman
            </label>
            <select
              value={perPage}
              onChange={(event) => mergeSearchParams({ per_page: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[20, 50, 100, 200].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Ekspor Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Kategori</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_categories)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Item Baris</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_items)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Qty</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_qty)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Nominal</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_amount)}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-sm font-semibold text-neutral-900">{reportData?.branch_name || "SEMUA CABANG"}</div>
          <div className="mt-1 text-xs text-neutral-500">LAPORAN PENJUALAN / GROUP</div>
          <div className="text-xs text-neutral-500">{reportData?.period_label || "-"}</div>
          <div className="text-xs text-neutral-500">Group : {reportData?.group_label || "Semua"}</div>
          <div className="text-xs text-neutral-500">User ID : {(reportData?.actor_name || "SYSTEM").toUpperCase()}</div>
        </div>

        <div className="overflow-x-auto">
          {isLoading && (
            <div className="px-4 py-10 text-center text-neutral-500">Memuat data laporan...</div>
          )}

          {!isLoading && groups.length === 0 && (
            <div className="px-4 py-10 text-center text-neutral-500">
              Tidak ada data penjualan untuk periode yang dipilih.
            </div>
          )}

          {!isLoading && groups.length > 0 && (
            <table className="min-w-full text-sm">
              <colgroup>
                <col style={{ width: "110px" }} />
                <col style={{ width: "180px" }} />
                <col />
                <col style={{ width: "80px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "100px" }} />
              </colgroup>
              <tbody>
                {groups.map((group, groupIndex) => (
                  <CategoryBlock key={`${group.group_id || "g"}-${groupIndex}`} group={group} />
                ))}

                {showGrandTotal && (
                  <tr className="border-t-2 border-neutral-400 bg-neutral-100">
                    <td colSpan={3} className="px-4 py-3 font-semibold text-right text-neutral-900">GRAND TOTAL</td>
                    <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_qty)}</td>
                    <td />
                    <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_amount)}</td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={paginationLinks}
            from={paginationFrom}
            to={paginationTo}
            total={pagination?.total}
          />
        </div>
      </div>
    </div>
  )
}

function CategoryBlock({ group }) {
  const items = Array.isArray(group?.items) ? group.items : []

  return (
    <>
      <tr className="bg-neutral-50 border-t border-neutral-200">
        <td className="px-4 py-2 font-semibold text-neutral-700">CATEGORY</td>
        <td colSpan={6} className="px-4 py-2 font-semibold text-neutral-900">{group?.group_name || "-"}</td>
      </tr>
      <tr className="bg-neutral-100 border-t border-neutral-200 text-neutral-700">
        <th className="px-4 py-2 font-semibold text-left">TGL</th>
        <th className="px-4 py-2 font-semibold text-left">NO. ORDER</th>
        <th className="px-4 py-2 font-semibold text-left">NAMA BARANG</th>
        <th className="px-4 py-2 font-semibold text-right">QTY</th>
        <th className="px-4 py-2 font-semibold text-right">HARGA</th>
        <th className="px-4 py-2 font-semibold text-right">TOTAL</th>
        <th className="px-4 py-2 font-semibold text-left">USER</th>
      </tr>
      {items.map((item, idx) => (
        <tr key={`${item.order_number || item.invoice_number || "inv"}-${idx}`} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
          <td className="px-4 py-2 text-neutral-800 whitespace-nowrap">{formatDateID(item.invoice_date)}</td>
          {/* ATALLA-346: tampilkan No Order (fallback ke invoice_number lama bila backend belum kirim). */}
          <td className="px-4 py-2 text-neutral-800 whitespace-nowrap">{item.order_number || item.invoice_number || "-"}</td>
          <td className="px-4 py-2 text-neutral-800 whitespace-normal break-words leading-5">{item.item_name || "-"}</td>
          <td className="px-4 py-2 text-right text-neutral-800">{formatNumber(item.qty)}</td>
          <td className="px-4 py-2 text-right text-neutral-800">
            {item.nett_price != null && Number(item.nett_price) < Number(item.price) ? (
              <span>
                <span className="mr-1 text-neutral-400 line-through">{formatNumber(item.price)}</span>
                <span className="font-semibold text-orange-600">{formatNumber(item.nett_price)}</span>
              </span>
            ) : (
              formatNumber(item.price)
            )}
          </td>
          <td className="px-4 py-2 font-semibold text-right text-neutral-900">{formatNumber(item.total)}</td>
          <td className="px-4 py-2 text-neutral-700 whitespace-nowrap">{item.user_name || ""}</td>
        </tr>
      ))}
      <tr className="border-t border-neutral-300 bg-neutral-50">
        <td colSpan={3} className="px-4 py-2 font-semibold text-right text-neutral-700">SUB TOTAL</td>
        <td className="px-4 py-2 font-semibold text-right text-neutral-900">{formatNumber(group?.subtotal_qty)}</td>
        <td />
        <td className="px-4 py-2 font-semibold text-right text-neutral-900">{formatNumber(group?.subtotal_amount)}</td>
        <td />
      </tr>
    </>
  )
}
